import { useState, useEffect } from 'react'
import { db } from '../firebase/config'

/**
 * De inzichten die daadwerkelijk gepubliceerd zijn — wat er buiten staat.
 *
 * Dit voedt het tabblad Publicaties. Die naam is bewust behouden: hij zegt dat
 * het naar buiten is gegaan, naar de marketingsite. Concepten horen er dus
 * NIET in; die bereik je via de knop op hun regel op het themadetail
 * (useThemeInsights kent ze wél).
 *
 * `loaded` en `error` zijn eigen toestanden, geen afgeleide van `insights.length`.
 * 'Er is nog niets gepubliceerd' is een bewering aan de lezer, en die is onwaar
 * zolang de read loopt of geweigerd werd.
 * AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 *
 * @returns {{insights: Array<object>, loaded: boolean, error: (Error|null)}}
 */
const usePublishedInsights = () => {
    const [insights, setInsights] = useState(/** @type {Array<object>} */([]))
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(/** @type {Error|null} */(null))

    useEffect(() => {
        setLoaded(false)
        setError(null)

        const unsub = db.collection('QuestionInsights')
            .where('Status', '==', 'published')
            .onSnapshot(
                (snap) => {
                    const gevonden = snap.docs
                        .map((doc) => ({ docid: doc.id, ...doc.data() }))
                        // Zacht verwijderd staat niet meer buiten.
                        .filter((insight) => insight.Deleted !== true)
                        // Nieuwste bovenaan. PublishedAt is een Firestore-Timestamp;
                        // toMillis bestaat pas als de server hem geschreven heeft,
                        // dus vlak na publiceren kan hij nog null zijn.
                        .sort((a, b) => (b.PublishedAt?.toMillis?.() || 0) - (a.PublishedAt?.toMillis?.() || 0)
                            || String(a.ID || '').localeCompare(String(b.ID || '')))
                    setInsights(gevonden)
                    setLoaded(true)
                },
                (err) => {
                    // Een mislukte listener is stil en ziet er precies uit als
                    // 'geen resultaten'. Zonder deze callback zou het tabblad
                    // beweren dat er niets gepubliceerd is.
                    // AI-CONTEXT: See docs/pitfalls.md#database-firestore-silent-listener-failure
                    console.error('usePublishedInsights:', err)
                    setError(err)
                    setInsights([])
                    setLoaded(false)
                },
            )

        return () => unsub()
    }, [])

    return { insights, loaded, error }
}

export default usePublishedInsights
